import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { checkUserExists } from '../../services/otpAuth';
import { PhoneIcon, KeyIcon } from '@heroicons/react/24/outline';
import { PainterTermsShort } from '../terms/PainterTermsShort';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { startOtpLogin, verifyOtpAndSignIn } = useAuth();
  
  const [formData, setFormData] = useState({
    phone: '',
    otp: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState<boolean | null>(null);
  const [showPhoneConfirm, setShowPhoneConfirm] = useState(false);

  // Resend timer countdown
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Format phone number for display (full number)
  const formatPhoneForDisplay = (phone: string) => {
    if (phone.length !== 10) return phone;
    // Show full number with spacing for readability: 9876 543 210
    return `${phone.slice(0, 4)} ${phone.slice(4, 7)} ${phone.slice(7)}`;
  };

  const handleGetOtpClick = async () => {
    if (!formData.phone || formData.phone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!agreed) {
      setError('Please agree to Terms & Conditions and Privacy Policy');
      return;
    }
    
    // Check for account type conflict BEFORE showing confirmation popup
    setLoading(true);
    setError('');
    try {
      const existsRes = await checkUserExists(formData.phone);
      
      // If there's a conflict (dealer account exists), stop and show error
      if (existsRes.conflict) {
        setError(existsRes.message || 'This phone number is registered as a Dealer account. Please use the Dealer app to login.');
        setLoading(false);
        return;
      }
      
      // No conflict - show confirmation popup
      setShowPhoneConfirm(true);
    } catch (error: any) {
      setError(error.message || 'Unable to verify phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSendOtp = async () => {
    setShowPhoneConfirm(false);
    await handleSendOtp();
  };

  const handleSendOtp = async () => {
    if (!formData.phone || formData.phone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    
    setLoading(true);
    try {
      // Check if user exists and account type before sending OTP
      const existsRes = await checkUserExists(formData.phone);
      
      // Check for account type conflict
      if (existsRes.conflict) {
        setError(existsRes.message || 'This phone number is registered with a different account type. Please use the correct app.');
        setLoading(false);
        return;
      }
      
      setIsExistingUser(!!existsRes.exists);

      const res = await startOtpLogin(formData.phone);
      if (res.success) {
        setOtpSent(true);
        setResendTimer(60);
      } else {
        setError(res.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const ok = await verifyOtpAndSignIn(formData.phone, formData.otp);
      if (!ok) {
        setError('Invalid OTP. Please try again.');
      } else if (isExistingUser === false) {
        // New user: signed in -> take to quick profile form
        navigate('/quick-profile', { replace: true, state: { phone: formData.phone } });
      } else {
        // Existing user: redirect to dashboard after successful login
        // Small delay to ensure auth state is updated
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      }
    } catch (error: any) {
      setError(error.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    // Check for account type conflict BEFORE showing confirmation popup
    setLoading(true);
    setError('');
    try {
      const existsRes = await checkUserExists(formData.phone);
      
      // If there's a conflict (dealer account exists), stop and show error
      if (existsRes.conflict) {
        setError(existsRes.message || 'This phone number is registered as a Dealer account. Please use the Dealer app to login.');
        setLoading(false);
        return;
      }
      
      // No conflict - show confirmation popup
      setShowPhoneConfirm(true);
    } catch (error: any) {
      setError(error.message || 'Unable to verify phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWrongNumber = () => {
    setOtpSent(false);
    setFormData(prev => ({ ...prev, otp: '' }));
    setError('');
    setResendTimer(0);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newOtp = formData.otp.split('');
    newOtp[index] = value;
    handleInputChange('otp', newOtp.join(''));

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden">
      <Helmet>
        <title>Login | BONDHU Painter</title>
        <meta name="description" content="Securely log in to your BONDHU Painter account. Verify your identity via OTP to access your rewards, scan tokens, and manage your painter profile." />
      </Helmet>
      {/* Hero Header */}
      <div className="bg-[#ee4238] text-white px-6 pt-14 pb-10 rounded-b-[28px]">
        <button
          onClick={() => navigate('/')}
          className="text-white/95 text-2xl font-bold mb-6"
          aria-label="Back"
        >
          ‹
        </button>
        <h1 className="text-3xl sm:text-4xl font-black leading-tight">BONDHU Painter<br />Verification</h1>
        <p className="text-white/90 mt-5 text-lg leading-relaxed">
          We will send you a One Time Password on your registered mobile number
        </p>
      </div>

      {/* Main Content */}
      <div className={`flex-1 px-6 pt-8 ${!otpSent ? 'pb-36' : 'pb-8'}`}> 
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-100 border-2 border-red-400 rounded-xl text-red-800 text-sm sm:text-base font-medium">
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-red-600 font-bold text-base sm:text-lg flex-shrink-0">⚠️</span>
              <span className="break-words leading-relaxed">{error}</span>
            </div>
          </div>
        )}

        {/* OTP Login */}
        <div className="space-y-6">
            {!otpSent ? (
              <>
                <div>
                  <label className="block text-[#111827] text-2xl font-black mb-3 leading-tight">
                    Enter mobile number
                  </label>
                  <div className="w-full rounded-2xl border border-gray-300 bg-white flex items-center px-4 py-3">
                    <span className="mr-3 text-xl" aria-hidden>🇮🇳</span>
                    <span className="mr-2 text-gray-900 text-xl sm:text-xl font-extrabold tracking-wide">+91</span>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('phone', value);
                      }}
                      className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-xl sm:text-xl font-extrabold tracking-wide"
                      placeholder="Enter 10-digit number"
                      maxLength={10}
                      required
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-gray-700 text-sm mb-4">
                    OTP sent to +91 {formData.phone}
                  </p>
                </div>

                <div>
                  <label className="block text-[#111827] text-sm font-medium mb-2">
                    <KeyIcon className="w-4 h-4 inline mr-2" />
                    Enter OTP
                  </label>
                  <div className="flex space-x-2 justify-center">
                    {[0,1,2,3,4,5].map((index) => (
                      <input
                        key={index}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={formData.otp[index] || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // Only allow numbers
                          handleOtpChange(index, value);
                        }}
                        id={`otp-${index}`}
                        className="w-12 h-12 text-center text-xl font-bold bg-white border border-gray-300 rounded text-gray-900 focus:ring-2 focus:ring-[#ee4238] focus:border-transparent"
                      />
                    ))}
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={handleVerifyOtp}
                    disabled={loading || formData.otp.length !== 6}
                    className="flex-1 bg-[#ee4238] text-white py-4 px-6 font-bold rounded-2xl hover:bg-[#d63a32] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'VERIFY'}
                  </button>
                  
                  <button
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0}
                    className="px-6 py-4 bg-transparent border border-gray-300 text-gray-800 font-bold rounded-2xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendTimer > 0 ? `${resendTimer}s` : 'RESEND'}
                  </button>
                </div>

                <div className="text-center mt-4">
                  <button
                    onClick={handleWrongNumber}
                    className="text-sm text-gray-600 underline hover:text-[#ee4238] transition-colors"
                  >
                    Wrong Number?
                  </button>
                </div>
              </>
            )}
            {/* Bottom fixed CTA and policy */}
            {!otpSent && (
              <div className="fixed inset-x-0 bottom-0 bg-white pt-3 pb-6 px-6 shadow-[0_-6px_20px_rgba(0,0,0,0.06)]">
                <label className="flex items-start text-gray-700 text-xs leading-snug mb-3 select-none">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 mr-2 h-4 w-4 rounded border-gray-300 text-[#ee4238] focus:ring-[#ee4238]"
                  />
                  <span>
                    I agree to the
                    <button onClick={() => setShowTerms(true)} className="mx-1 underline">Terms of Service</button>
                    and
                    <button onClick={() => setShowPrivacy(true)} className="ml-1 underline">Privacy Policy</button>
                  </span>
                </label>
                <button
                  onClick={handleGetOtpClick}
                  disabled={loading || formData.phone.length !== 10 || !agreed}
                  className="w-full bg-[#ee4238] text-white py-4 px-6 font-extrabold text-xl rounded-2xl hover:bg-[#d63a32] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Get OTP'}
                </button>
              </div>
            )}
          </div>
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setShowTerms(false)}>
          <div className="bg-white w-full sm:max-w-lg max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-6 text-left" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-black mb-4">Terms & Conditions</h2>
            <PainterTermsShort />
            <div className="mt-4 text-center">
              <Link 
                to="/terms" 
                onClick={() => setShowTerms(false)}
                className="text-[#ee4238] underline text-sm font-medium"
              >
                View Full Terms & Conditions
              </Link>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowTerms(false)} className="px-4 py-2 bg-[#ee4238] text-white rounded-md">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal (brief) */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setShowPrivacy(false)}>
          <div className="bg-white w-full sm:max-w-lg max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-6 text-left" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-black mb-4">Privacy Policy</h2>
            <p className="text-sm text-gray-800 leading-relaxed">
              We value your privacy. Your personal information is collected for verification and payout purposes and is stored securely. We do not sell your data. We may share information with payment partners and service providers solely to enable payouts and communications, or when required by law.
            </p>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowPrivacy(false)} className="px-4 py-2 bg-[#ee4238] text-white rounded-md">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Number Confirmation Modal */}
      {showPhoneConfirm && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-3 sm:p-4 md:p-6" 
          onClick={() => setShowPhoneConfirm(false)}
        >
          <div 
            className="bg-white w-full sm:max-w-md md:max-w-lg rounded-t-3xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 text-left shadow-2xl max-h-[90vh] overflow-y-auto modal-slide-up" 
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl font-black mb-4 sm:mb-5 text-gray-900 text-center sm:text-left">
              Confirm Phone Number
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-4 sm:mb-5 md:mb-6 text-center sm:text-left">
              OTP will be sent to:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 mb-5 sm:mb-6 md:mb-8">
              <p className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 text-center tracking-wide break-words">
                +91 {formatPhoneForDisplay(formData.phone)}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 md:gap-4">
              <button
                onClick={() => setShowPhoneConfirm(false)}
                className="w-full sm:flex-1 px-5 sm:px-6 py-3 sm:py-3.5 md:py-4 bg-gray-100 text-gray-800 text-sm sm:text-base md:text-lg font-bold rounded-xl sm:rounded-2xl hover:bg-gray-200 active:bg-gray-300 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendOtp}
                disabled={loading}
                className="w-full sm:flex-1 px-5 sm:px-6 py-3 sm:py-3.5 md:py-4 bg-[#ee4238] text-white text-sm sm:text-base md:text-lg font-bold rounded-xl sm:rounded-2xl hover:bg-[#d63a32] active:bg-[#c4342a] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#ee4238]"
              >
                {loading ? 'Sending...' : 'Confirm & Send OTP'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
